// The Analytics tab of a club / athlete cabinet (BAT-1102 owner endpoints,
// not in the generated client yet). Pure helpers live here; the queries are
// in hooks/useClubAnalytics.

export type AnalyticsShare = { key: string; count: number; percent: number }
export type AnalyticsCountry = {
  iso2?: string
  name: string
  count: number
  percent: number
}

export type AnalyticsAudience = {
  total_fans: number
  gender: AnalyticsShare[]
  age: AnalyticsShare[]
  activity: AnalyticsShare[]
  location: AnalyticsCountry[]
  /** Fans with a known country outside the top ones. */
  location_other: { count: number; percent: number }
}

export const ANALYTICS_AUDIENCE_KEY = 'club_analytics_audience'
export const ANALYTICS_FAN_GROWTH_KEY = 'club_analytics_fan_growth'

/** The categories of each block, in the design's order (US-ATH-08.2). */
export const GENDER_KEYS = ['female', 'male', 'other'] as const
export const AGE_KEYS = ['18-25', '26-40', '41-55'] as const
export const ACTIVITY_KEYS = ['none', '1-3', '4-6', '7+'] as const
/** Location shows the top 5 countries and Other (design). */
export const LOCATION_LIMIT = 5

const num = (value: unknown): number =>
  Number.isFinite(Number(value)) ? Number(value) : 0

/**
 * A distribution block in its fixed order: categories the backend left out
 * count 0, so a club without fans still shows every row (US-ATH-08.2).
 */
export function orderedShares(
  shares: AnalyticsShare[] | undefined,
  keys: readonly string[]
): AnalyticsShare[] {
  const byKey = new Map((shares ?? []).map(share => [share.key, share]))
  return keys.map(key => {
    const share = byKey.get(key)
    return {
      key,
      count: Math.max(0, num(share?.count)),
      percent: Math.max(0, num(share?.percent))
    }
  })
}

export function normaliseAudience(raw: unknown): AnalyticsAudience {
  const r = (raw ?? {}) as Partial<AnalyticsAudience>
  const location = (r.location ?? [])
    .map(country => ({
      iso2: country.iso2?.trim().toUpperCase() || undefined,
      name: country.name ?? '',
      count: Math.max(0, num(country.count)),
      percent: Math.max(0, num(country.percent))
    }))
    .sort((a, b) => b.count - a.count || b.percent - a.percent)
    .slice(0, LOCATION_LIMIT)
  return {
    total_fans: Math.max(0, num(r.total_fans)),
    gender: orderedShares(r.gender, GENDER_KEYS),
    age: orderedShares(r.age, AGE_KEYS),
    activity: orderedShares(r.activity, ACTIVITY_KEYS),
    location,
    location_other: {
      count: Math.max(0, num(r.location_other?.count)),
      percent: Math.max(0, num(r.location_other?.percent))
    }
  }
}

/** The category to highlight: the largest one, none when all are empty. */
export function maxShareKey(shares: AnalyticsShare[]): string | null {
  let best: AnalyticsShare | null = null
  for (const share of shares) {
    if (share.count > 0 && (!best || share.count > best.count)) {
      best = share
    }
  }
  return best?.key ?? null
}

/** «12%», «12.5%» — one decimal at most, as the backend rounds. */
export function formatPercent(percent: number): string {
  const rounded = Math.round(percent * 10) / 10
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`
}
